import { useCallback, useEffect, useState } from "react";
import { CloudLightning, Loader2, Mail, ClipboardList, Sparkles, History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SECTIONS = ["Current Conditions", "Tropical Tracking", "Potential Impact",
                  "Operational Considerations", "Recommended Actions"];

const SEVERITY = {
  warning: "bg-red-600 text-white",
  watch: "bg-amber-500 text-royal-deep",
  advisory: "bg-yellow-200 text-royal-deep",
  monitor: "bg-royal/10 text-royal",
  normal: "bg-emerald-50 text-emerald-800",
};

/** The automated property weather briefing: conditions and tropical tracking straight from the
 *  official record, the operational read authored from those same facts, one click to the owner,
 *  and the recommended actions can become real work orders. */
export function WeatherBriefing({ propertyId, ownerName, ownerEmail, canManage }) {
  const [brief, setBrief] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState("");
  const [picked, setPicked] = useState([]);

  const load = useCallback(() => {
    if (!propertyId) return;
    api.get(`/weather/property/${propertyId}/briefing`)
      .then(({ data }) => {
        setBrief(data || null);
        setPicked(data?.actions || []);
      })
      .catch(() => setBrief(null));
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post(`/weather/property/${propertyId}/briefing`);
      setBrief(data);
      setPicked(data.actions || []);
      toast.success("Weather briefing written from the official data");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not write the briefing");
    }
    setBusy("");
  };

  const emailOwner = async () => {
    setBusy("email");
    try {
      const { data } = await api.post(`/weather/briefing/${brief._id}/email`);
      setBrief({ ...brief, emailed_at: data.emailed_at, emailed_to: data.to });
      toast.success(`Briefing sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the briefing");
    }
    setBusy("");
  };

  const raiseWork = async () => {
    setBusy("work");
    try {
      const { data } = await api.post(`/weather/briefing/${brief._id}/work-orders`,
                                      { actions: picked });
      toast.success(`${data.count} work order${data.count === 1 ? "" : "s"} created`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create the work orders");
    }
    setBusy("");
  };

  const openHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !history.length) {
      const { data } = await api.get(`/weather/property/${propertyId}/briefings`);
      setHistory(data || []);
    }
  };

  const toggle = (action) =>
    setPicked((p) => (p.includes(action) ? p.filter((a) => a !== action) : [...p, action]));

  return (
    <div className="rounded-lg border border-gold/40 bg-royal-deep p-4 sm:p-5 min-w-0 overflow-hidden"
         data-testid="weather-briefing-card">
      <div className="flex flex-wrap items-center gap-2">
        <CloudLightning className="h-4 w-4 text-gold" />
        <h3 className="font-display text-base sm:text-lg font-semibold text-white">
          Property weather briefing
        </h3>
        {brief?.severity && (
          <span className={`ml-auto rounded-full px-2.5 py-1 text-[10px] font-bold uppercase
                            tracking-[0.14em] ${SEVERITY[brief.severity] || SEVERITY.normal}`}
                data-testid="briefing-severity">
            {brief.severity}
          </span>
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-blue-100/60">
        Conditions, alerts and tropical tracking exactly as published by the National Weather
        Service and the National Hurricane Center. Operational read is Cornerstone Ops analysis.
      </p>

      {!brief ? (
        <p className="mt-4 text-sm text-blue-100/70" data-testid="briefing-empty">
          No weather briefing yet. One is written automatically the moment an official
          warning, watch, tropical threat or freeze risk lands on this property.
        </p>
      ) : (
        <div className="mt-4 space-y-3.5" data-testid="briefing-sections">
          {SECTIONS.map((name) => {
            const body = brief.sections?.[name];
            if (!body) return null;
            const isActions = name === "Recommended Actions";
            return (
              <div key={name} data-testid={`briefing-section-${name.toLowerCase().replace(/ /g, "-")}`}>
                <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.2em] text-gold/90">
                  {name}
                </p>
                {isActions && canManage ? (
                  <div className="mt-1.5 space-y-1.5">
                    {(brief.actions || []).map((action) => (
                      <label key={action}
                             className="flex items-start gap-2 text-[12px] sm:text-sm text-blue-50/95
                                        cursor-pointer">
                        <input type="checkbox" checked={picked.includes(action)}
                               onChange={() => toggle(action)}
                               data-testid={`briefing-action-${action.slice(0, 18).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                               className="mt-1 h-3.5 w-3.5 accent-gold" />
                        <span>{action}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  body.split("\n").map((line, i) => (
                    <p key={i} className="mt-1 text-[12px] sm:text-sm leading-relaxed text-blue-50/95">
                      {line}
                    </p>
                  ))
                )}
              </div>
            );
          })}
          <p className="pt-1 text-[11px] text-blue-100/55" data-testid="briefing-provenance">
            {brief.automated ? "Written automatically" : `Written by ${brief.generated_by}`}
            {brief.generated_at && ` · ${new Date(brief.generated_at).toLocaleString()}`}
            {brief.weather_status && ` · official data ${brief.weather_status}`}
          </p>
          {brief.emailed_at && (
            <p className="text-[11px] text-gold" data-testid="briefing-emailed">
              Sent to {brief.emailed_to} on {new Date(brief.emailed_at).toLocaleString()}
            </p>
          )}
          {!!brief.work_order_ids?.length && (
            <p className="text-[11px] text-blue-100/70" data-testid="briefing-work-orders">
              {brief.work_order_ids.length} work order
              {brief.work_order_ids.length === 1 ? "" : "s"} raised from this briefing
            </p>
          )}
        </div>
      )}

      {canManage && (
        <div className="mt-4 space-y-2">
          <Button data-testid="generate-weather-briefing-btn" onClick={generate}
                  disabled={!!busy}
                  className="w-full bg-gold hover:bg-gold/90 text-royal-deep font-semibold">
            {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><Sparkles className="h-4 w-4 mr-2" />{brief ? "Rewrite from the latest data" : "Write the briefing"}</>}
          </Button>
          {brief && (
            <>
              <Button data-testid="email-weather-briefing-btn" onClick={emailOwner}
                      disabled={!!busy || !ownerEmail} variant="outline"
                      className="w-full border-gold/40 bg-transparent text-white hover:bg-white/10 font-semibold">
                {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Mail className="h-4 w-4 mr-2" /> Email to owner</>}
              </Button>
              <p className="text-[11px] text-blue-100/55" data-testid="weather-briefing-recipient">
                {ownerEmail ? `Goes to ${ownerName || "the owner"} at ${ownerEmail}`
                  : "Assign an owner with an email address to send this"}
              </p>
              <Button data-testid="briefing-work-orders-btn" onClick={raiseWork}
                      disabled={!!busy || !picked.length} variant="outline"
                      className="w-full border-gold/40 bg-transparent text-white hover:bg-white/10 font-semibold">
                {busy === "work" ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><ClipboardList className="h-4 w-4 mr-2" />
                      Create {picked.length || ""} work order{picked.length === 1 ? "" : "s"}</>}
              </Button>
              <button data-testid="briefing-history-btn" onClick={openHistory}
                      className="flex items-center gap-1.5 pt-1 text-[11px] font-semibold uppercase
                                 tracking-[0.14em] text-gold/80 transition-colors hover:text-gold">
                <History className="h-3.5 w-3.5" /> {showHistory ? "Hide" : "Briefing history"}
              </button>
            </>
          )}
          {showHistory && (
            <div className="space-y-1.5 pt-1" data-testid="briefing-history">
              {history.length <= 1 ? (
                <p className="text-[11px] text-blue-100/55">No earlier briefings yet.</p>
              ) : history.slice(1).map((h) => (
                <p key={h._id} className="text-[11px] text-blue-100/70">
                  {new Date(h.generated_at).toLocaleString()} · {h.trigger}
                  {h.emailed_at ? " · sent to the owner" : ""}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
